<script setup lang="ts">
  import { computed, onMounted, ref, watch, nextTick } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { ROUTES } from '@/router/routes'
  import {
    QuestionnairesService,
    type CustomerOrganisation as Organisation,
    type SuperUserQuestionnaire as Questionnaire
  } from '@/types/generated'
  import { BoltIcon, PencilIcon, DocumentDuplicateIcon } from '@heroicons/vue/24/outline'
  import {
    ChevronLeft,
    ChevronRight,
    ChevronsRight,
    FileText,
    Plus,
    CheckCircle2,
    X,
    Copy,
    WandSparkles
  } from 'lucide-vue-next'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger
  } from '@/components/ui/dialog'
  import Button from '@/components/ui/button/Button.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import SelectField, { type Option } from '@/components/admin/form/SelectField.vue'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import FileField from '@/components/admin/form/FileField.vue'
  import { Separator } from '@/components/ui/separator'
  import { useQuestionnaireEditorStore } from '@/store/admin/questionnaire/questionnaireEditor'
  import UserQuestionnaireSidebar from '@/components/app/questionnaire/editor/UserQuestionnaireSidebar.vue'
  import UserQuestionnaireSection from '@/components/app/questionnaire/editor/UserQuestionnaireSection.vue'
  import UserQuestionnaireQuestionView from '@/components/app/questionnaire/editor/UserQuestionnaireQuestionView.vue'
  import UserQuestionnaireVirtualQuestionView from '@/components/app/questionnaire/editor/UserQuestionnaireVirtualQuestionView.vue'
  import { genericErrorToast, saveSuccessToast } from '@/lib/toasts'
  import { toast } from '@/components/ui/toast'
  import { useTranslation } from '@/composables/useTranslation'
  import { useUserStore } from '@/shared/stores/userStore'
  import UnsavedChangesDialog from '@/components/ui/UnsavedChangesDialog.vue'
  import ValidationErrorDialog, {
    type ValidationErrorItem
  } from '@/components/ui/ValidationErrorDialog.vue'
  import QuestionnaireFlowPreviewDialog from '@/components/app/questionnaire/editor/QuestionnaireFlowPreviewDialog.vue'
  import { Play } from 'lucide-vue-next'
  import {
    onBeforeRouteLeave,
    type RouteLocationNormalized,
    type NavigationGuardNext
  } from 'vue-router'

  const { t } = useTranslation({
    en: {
      newQuestionnaireHeader: 'New questionnaire header',
      backToAllQuestionnaires: 'Back to all questionnaires',
      newQuestionnaire: 'New questionnaire',
      generateWithAI: 'Generate with AI',
      preview: 'Preview',
      duplicate: 'Duplicate',
      saveQuestionnaire: 'Save questionnaire',
      sections: 'Sections',
      questionnaireDetails: 'Questionnaire details',
      generalInformation: 'General information',
      title: 'Title',
      lawField: 'Law field',
      lawTopic: 'Law topic',
      internalDescription: 'Internal description',
      notSelected: 'Not selected',
      collapseAllSections: 'Collapse all sections',
      hideSidebar: 'Hide sidebar',
      showSidebar: 'Show sidebar',
      expandAll: 'Expand all',
      addSection: 'Add section',
      addQuestion: 'Add question',
      create: 'Create',
      createWithAI: 'Create questionnaire with AI',
      creatingQuestionnaire: 'Creating questionnaire, this may take a while...',
      enterUserPrompt: 'Enter AI prompt',
      questionnaireCreated: 'Questionnaire created with AI',
      questionnaireCreatedDescription:
        'The questionnaire has been created with AI, it is however not yet saved',
      uploadFile: 'Upload file',
      uploadToCreateQuestionnaire: 'Upload a pdf/word file to create a questionnaire using AI',
      uploadHint: 'Upload a pdf/word file or drag and drop it here',
      userPrompt: 'AI Prompt',
      or: 'or',
      changesNotSaved: 'Changes not saved',
      unsavedChangesDescription: 'Are you sure you want to leave without saving your changes?',
      discardChanges: 'Discard changes',
      goBack: 'Go back',
      validationError: 'Validation Error',
      errorInSection: 'Error in section "{section}"',
      errorInQuestion: 'Error in question "{question}"',
      goToError: 'Go to error',
      fieldRequired: 'This field is required',
      invalidValue: 'Invalid value',
      error: 'Error',
      questionnaireNotFound: 'Questionnaire not found',
      titleUpdated: 'Title updated',
      titleUpdatedDescription: 'Questionnaire title has been saved successfully',
      selectSectionFromSidebar: 'Please select a section from the sidebar',
      newQuestionnairePlaceholder: 'New questionnaire'
    },
    de: {
      newQuestionnaireHeader: 'Neuer Fragebogen-Header',
      backToAllQuestionnaires: 'Zurück zu allen Fragebögen',
      newQuestionnaire: 'Neuer Fragebogen',
      generateWithAI: 'Mit KI generieren',
      preview: 'Vorschau',
      duplicate: 'Duplizieren',
      saveQuestionnaire: 'Fragebogen speichern',
      sections: 'Abschnitte',
      questionnaireDetails: 'Fragebogen-Details',
      generalInformation: 'Allgemeine Informationen',
      title: 'Titel',
      lawField: 'Rechtsgebiet',
      lawTopic: 'Rechtsthema',
      internalDescription: 'Interne Beschreibung',
      notSelected: 'Nicht ausgewählt',
      collapseAllSections: 'Alle Abschnitte einklappen',
      hideSidebar: 'Seitenleiste ausblenden',
      showSidebar: 'Seitenleiste anzeigen',
      expandAll: 'Alle ausklappen',
      addSection: 'Abschnitt hinzufügen',
      addQuestion: 'Frage hinzufügen',
      create: 'Erstellen',
      createWithAI: 'Fragebogen mit KI erstellen',
      creatingQuestionnaire: 'Fragebogen wird erstellt, dies kann einige Zeit dauern...',
      enterUserPrompt: 'KI Prompt eingeben',
      questionnaireCreated: 'Fragebogen mit KI erstellt',
      questionnaireCreatedDescription:
        'Der Fragebogen wurde mit KI erstellt, ist jedoch noch nicht gespeichert',
      uploadFile: 'Datei hochladen',
      uploadToCreateQuestionnaire:
        'Laden Sie eine PDF-/Word-Datei hoch, um einen Fragebogen mit KI zu erstellen',
      uploadHint: 'Laden Sie eine PDF-/Word-Datei hoch oder ziehen Sie sie hierher',
      userPrompt: 'KI Prompt',
      or: 'oder',
      changesNotSaved: 'Änderungen nicht gespeichert',
      unsavedChangesDescription: 'Sind Sie sicher, dass Sie ohne Speichern verlassen möchten?',
      discardChanges: 'Änderungen verwerfen',
      goBack: 'Zurück',
      validationError: 'Validierungsfehler',
      errorInSection: 'Fehler im Abschnitt „{section}"',
      errorInQuestion: 'Fehler bei der Frage „{question}"',
      goToError: 'Zum Fehler',
      fieldRequired: 'Dieses Feld ist erforderlich',
      invalidValue: 'Ungültiger Wert',
      error: 'Fehler',
      questionnaireNotFound: 'Fragebogen nicht gefunden',
      titleUpdated: 'Titel aktualisiert',
      titleUpdatedDescription: 'Fragebogen-Titel wurde erfolgreich gespeichert',
      selectSectionFromSidebar: 'Bitte wählen Sie einen Abschnitt aus der Seitenleiste',
      newQuestionnairePlaceholder: 'Neuer Fragebogen'
    }
  })

  const route = useRoute()
  const router = useRouter()
  const questionnaireEditorStore = useQuestionnaireEditorStore()
  const { user } = useUserStore()
  const questionnaire = computed<Questionnaire | null>(() => questionnaireEditorStore.questionnaire)
  const categoryOptions = computed<Option[]>(() => questionnaireEditorStore.categoryOptions)
  const lawFieldsOptions = computed<Option[]>(() => questionnaireEditorStore.lawFieldsOptions)
  const lawTopicOptions = computed<Option[]>(() => questionnaireEditorStore.lawTopicOptions)
  const selectedItem = computed(() => questionnaireEditorStore.selectedItem)

  // Check if this is a new questionnaire
  const isNewQuestionnaire = computed(() => route.params.uuid === 'new')

  // Helper function to find a question by UUID
  const getQuestionByUuid = (uuid: string) => {
    if (!questionnaire.value?.schema) return null

    for (const section of questionnaire.value.schema) {
      for (const question of section.items || []) {
        if (question.uuid === uuid) {
          return question
        }
      }
    }
    return null
  }

  // Helper function to check if selected item is a virtual question and get its section UUID
  const getVirtualQuestionSectionUuid = (selectedItemValue: string): string | null => {
    if (!selectedItemValue.startsWith('virtual-question-')) return null
    return selectedItemValue.replace('virtual-question-', '')
  }

  const uploadedFile = ref<File | null>(null)
  const userPrompt = ref('')
  const isSidebarOpen = ref(true)
  const isModalOpen = ref(false)
  const isLoading = ref(false)
  const hasChanges = ref(false)

  // Validation error dialog state
  const showValidationErrorDialog = ref(false)
  const validationErrors = ref<ValidationErrorItem[]>([])

  // Flow preview dialog state
  const showFlowPreviewDialog = ref(false)

  // Title editing state
  const isEditingTitle = ref(false)
  const editingTitle = ref('')
  const titleInput = ref<HTMLInputElement | null>(null)

  // Unsaved changes dialog state
  const dialogState = {
    show: ref(false),
    onConfirm: ref<(() => void) | null>(null),
    onCancel: ref<(() => void) | null>(null)
  }

  // Store pending navigation info
  const pendingNavigation = ref<{
    to: RouteLocationNormalized
    next: NavigationGuardNext
  } | null>(null)

  // Flag to temporarily disable the guard
  const guardDisabled = ref(false)

  // Track if the questionnaire has been initially loaded
  const initialLoadComplete = ref(false)

  watch(
    questionnaire,
    (newValue, oldValue) => {
      // Only mark as changed after initial load and if there's an actual change
      if (initialLoadComplete.value && oldValue !== null && newValue !== null) {
        hasChanges.value = true
      }
    },
    { deep: true }
  )

  // Clear law_topic when law_field changes to prevent invalid combinations
  watch(
    () => questionnaire.value?.law_field,
    (newLawField, oldLawField) => {
      // Only clear if we have an actual change (not initial load) and questionnaire exists
      if (
        initialLoadComplete.value &&
        newLawField !== oldLawField &&
        questionnaire.value &&
        questionnaire.value.law_topic
      ) {
        // Check if the current law_topic is valid for the new law_field
        const isValidTopic = lawTopicOptions.value.some(
          (option) => option.value === questionnaire.value?.law_topic
        )
        if (!isValidTopic) {
          questionnaire.value.law_topic = ''
        }
      }
    }
  )

  // Navigation guard for unsaved changes
  onBeforeRouteLeave(
    (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
      // Show dialog if there are changes OR if it's a new questionnaire (unsaved)
      const hasUnsavedWork = hasChanges.value || isNewQuestionnaire.value

      if (guardDisabled.value || !hasUnsavedWork) {
        return next()
      }

      pendingNavigation.value = { to, next }

      dialogState.onConfirm.value = () => {
        if (pendingNavigation.value) {
          const { to } = pendingNavigation.value
          pendingNavigation.value = null

          guardDisabled.value = true
          router
            .push(to.fullPath)
            .then(() => {
              guardDisabled.value = false
            })
            .catch(() => {
              guardDisabled.value = false
            })
        }
      }

      dialogState.onCancel.value = () => {
        pendingNavigation.value = null
      }

      dialogState.show.value = true
      return next(false)
    }
  )

  onMounted(async () => {
    await questionnaireEditorStore.init(route.params.uuid as string)
    // Mark initial load as complete
    initialLoadComplete.value = true
  })

  const saveQuestionnaire = async (
    from_ai: boolean = false,
    has_file: boolean = false,
    has_prompt: boolean = false,
    silent: boolean = false
  ) => {
    try {
      if (isNewQuestionnaire.value) {
        // Create new questionnaire
        const { uuid, ...requestBodyWithoutUuid } = questionnaire.value!
        const requestBody = {
          ...requestBodyWithoutUuid,
          org: user?.org?.uuid || null,
          is_from_ai: from_ai,
          has_file: has_file,
          has_prompt: has_prompt
        }

        const newQuestionnaire = await QuestionnairesService.questionnairesCreate({
          requestBody
        })

        // Update the store with the newly created questionnaire
        if (questionnaire.value) {
          Object.assign(questionnaire.value, newQuestionnaire)
        }

        // Only navigate and show toast if not in silent mode (for AI generation flow)
        if (!silent) {
          // Navigate to the newly created questionnaire's URL
          router.replace(
            ROUTES.APP.SETTINGS.QUESTIONNAIRES.QUESTIONNAIRE_DETAIL.replace(
              ':uuid',
              newQuestionnaire.uuid
            )
          )

          toast({
            title: t('questionnaireCreated'),
            description: t('questionnaireCreatedDescription'),
            duration: 2000
          })
        }
      } else {
        // Update existing questionnaire
        await QuestionnairesService.questionnairesUpdate({
          uuid: questionnaire.value!.uuid,
          requestBody: questionnaire.value!
        })
        saveSuccessToast()
      }
      hasChanges.value = false
    } catch (error: any) {
      handleSaveError(error)
    }
  }

  const parseValidationErrors = (errorBody: any): ValidationErrorItem[] => {
    const errors: ValidationErrorItem[] = []

    // Handle top-level field errors (law_field, law_topic, title, etc.)
    Object.entries(errorBody).forEach(([key, value]) => {
      if (key === 'schema') return // Handle schema separately

      if (Array.isArray(value)) {
        errors.push({
          message: `${formatFieldName(key)}: ${value.join(', ')}`,
          field: key
        })
      } else if (typeof value === 'object' && value !== null) {
        // Handle nested object errors
        const nestedErrors = Object.entries(value)
          .filter(([, v]) => Array.isArray(v))
          .map(([, v]) => (v as string[]).join(', '))
          .join('; ')
        if (nestedErrors) {
          errors.push({
            message: `${formatFieldName(key)}: ${nestedErrors}`,
            field: key
          })
        }
      }
    })

    // Handle schema errors (section and question level)
    if (errorBody.schema && Array.isArray(errorBody.schema)) {
      errorBody.schema.forEach((sectionErrors: any, sectionIndex: number) => {
        if (!sectionErrors || Object.keys(sectionErrors).length === 0) return

        const section = questionnaire.value?.schema?.[sectionIndex]
        const sectionName = section?.title || `Section ${sectionIndex + 1}`
        const sectionUuid = section?.uuid

        // Handle section-level errors (non_field_errors, etc.)
        if (sectionErrors.non_field_errors && Array.isArray(sectionErrors.non_field_errors)) {
          sectionErrors.non_field_errors.forEach((msg: string) => {
            errors.push({
              message: msg,
              sectionIndex,
              sectionUuid,
              sectionName
            })
          })
        }

        // Handle other section-level field errors
        Object.entries(sectionErrors).forEach(([key, value]) => {
          if (key === 'items' || key === 'non_field_errors') return
          if (Array.isArray(value)) {
            errors.push({
              message: `${formatFieldName(key)}: ${value.join(', ')}`,
              sectionIndex,
              sectionUuid,
              sectionName,
              field: key
            })
          }
        })

        // Handle item/question-level errors
        if (sectionErrors.items && Array.isArray(sectionErrors.items)) {
          sectionErrors.items.forEach((itemErrors: any, itemIndex: number) => {
            if (!itemErrors || Object.keys(itemErrors).length === 0) return

            const question = section?.items?.[itemIndex]
            const questionName = question?.label
              ? question.label.length > 40
                ? `${question.label.substring(0, 40)}...`
                : question.label
              : `Question ${itemIndex + 1}`
            const questionUuid = question?.uuid

            // Handle non_field_errors at question level
            if (itemErrors.non_field_errors && Array.isArray(itemErrors.non_field_errors)) {
              itemErrors.non_field_errors.forEach((msg: string) => {
                errors.push({
                  message: msg,
                  sectionIndex,
                  sectionUuid,
                  sectionName,
                  questionIndex: itemIndex,
                  questionUuid,
                  questionName
                })
              })
            }

            // Handle field-specific errors
            Object.entries(itemErrors).forEach(([key, value]) => {
              if (key === 'non_field_errors') return
              if (Array.isArray(value)) {
                errors.push({
                  message: `${formatFieldName(key)}: ${value.join(', ')}`,
                  sectionIndex,
                  sectionUuid,
                  sectionName,
                  questionIndex: itemIndex,
                  questionUuid,
                  questionName,
                  field: key
                })
              }
            })
          })
        }
      })
    }

    return errors
  }

  const formatFieldName = (field: string): string => {
    // Convert snake_case to Title Case
    return field
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const navigateToError = (error: ValidationErrorItem) => {
    if (error.questionUuid) {
      questionnaireEditorStore.selectQuestion(error.questionUuid)
    } else if (error.sectionUuid) {
      questionnaireEditorStore.selectSection(error.sectionUuid)
    } else if (
      error.field === 'law_field' ||
      error.field === 'law_topic' ||
      error.field === 'title'
    ) {
      questionnaireEditorStore.selectQuestionnaireDetails()
    }
  }

  const handleSaveError = (error: any) => {
    console.error('Error saving questionnaire:', error)

    if (error.body) {
      const errors = parseValidationErrors(error.body)

      if (errors.length > 0) {
        // Show validation error dialog
        validationErrors.value = errors
        showValidationErrorDialog.value = true
      } else {
        // Fallback for unknown error structure
        toast({
          title: t('validationError'),
          description: JSON.stringify(error.body, null, 2),
          variant: 'destructive',
          duration: 10000
        })
      }
    } else {
      genericErrorToast()
    }
  }

  const sendFile = async () => {
    if (!questionnaire.value) {
      toast({
        title: t('error'),
        description: t('questionnaireNotFound'),
        duration: 3000
      })
      return
    }

    // Set loading state immediately so the modal shows the loading spinner
    isLoading.value = true

    try {
      const wasNewQuestionnaire = isNewQuestionnaire.value

      // For new questionnaires, we need to save first to get a UUID
      // Use silent mode to avoid showing toast and navigating before AI generation completes
      if (wasNewQuestionnaire) {
        // Clear the default schema before saving - the AI will generate the sections
        if (questionnaire.value) {
          questionnaire.value.schema = []
        }
        const has_file = uploadedFile.value ? true : false
        const has_prompt = userPrompt.value ? true : false
        await saveQuestionnaire(true, has_file, has_prompt, true)
      }

      const response = (await QuestionnairesService.questionnairesGenerateCreate({
        uuid: questionnaire.value.uuid,
        formData: {
          file: uploadedFile.value!,
          prompt: userPrompt.value ?? ''
        }
      })) as Questionnaire

      // Update the store with the AI response
      questionnaireEditorStore.updateFromAiResponse(response)

      // Save the AI-generated content to the server before navigation
      // This is necessary because App.vue has :key="$route.fullPath" on RouterView,
      // which causes the component to remount on navigation, triggering a re-fetch from server
      if (questionnaire.value) {
        await QuestionnairesService.questionnairesUpdate({
          uuid: questionnaire.value.uuid,
          requestBody: questionnaire.value
        })
      }

      toast({
        title: t('questionnaireCreated'),
        description: t('questionnaireCreatedDescription'),
        duration: 3000
      })

      // Navigate to the newly created questionnaire's URL after AI generation completes
      if (wasNewQuestionnaire && questionnaire.value) {
        // Reset hasChanges and disable guard before navigation to prevent the unsaved changes dialog
        hasChanges.value = false
        guardDisabled.value = true
        router
          .replace(
            ROUTES.APP.SETTINGS.QUESTIONNAIRES.QUESTIONNAIRE_DETAIL.replace(
              ':uuid',
              questionnaire.value.uuid
            )
          )
          .finally(() => {
            guardDisabled.value = false
          })
      }
    } catch (error) {
      console.error('Error creating questionnaire with AI:', error)
      genericErrorToast()
    } finally {
      uploadedFile.value = null
      isModalOpen.value = false
      isLoading.value = false
    }
  }

  const toggleSidebar = () => {
    isSidebarOpen.value = !isSidebarOpen.value
  }

  const duplicateQuestionnaire = async () => {
    if (questionnaire.value) {
      try {
        // For new questionnaires, we need to save first to get a UUID
        if (isNewQuestionnaire.value) {
          await saveQuestionnaire()
        } else {
          // Save current state before duplicating
          await saveQuestionnaire()
        }

        // Create duplicate
        const copy = await QuestionnairesService.questionnairesCopyCreate({
          uuid: questionnaire.value.uuid
        })

        if (copy) {
          // Navigate to the duplicated questionnaire
          router.push(
            ROUTES.APP.SETTINGS.QUESTIONNAIRES.QUESTIONNAIRE_DETAIL.replace(':uuid', copy.uuid)
          )
        }
      } catch (error) {
        console.error('Error duplicating questionnaire:', error)
        genericErrorToast()
      }
    }
  }

  // Title editing functions
  const startEditingTitle = async () => {
    isEditingTitle.value = true
    editingTitle.value = questionnaire.value?.title || ''
    await nextTick()
    titleInput.value?.focus()
    titleInput.value?.select()
  }

  const saveTitle = async () => {
    if (editingTitle.value.trim() && questionnaire.value) {
      try {
        // Update the title in memory first
        questionnaire.value.title = editingTitle.value.trim()

        if (!isNewQuestionnaire.value) {
          // Save to backend immediately for existing questionnaires
          await QuestionnairesService.questionnairesUpdate({
            uuid: questionnaire.value.uuid,
            requestBody: questionnaire.value
          })

          // Show success feedback
          toast({
            title: t('titleUpdated'),
            description: t('titleUpdatedDescription'),
            duration: 2000
          })

          hasChanges.value = false
        } else {
          // For new questionnaires, just mark as changed
          hasChanges.value = true
        }
      } catch (error) {
        console.error('Error saving title:', error)
        genericErrorToast()
      }
    }
    cancelEditingTitle()
  }

  const cancelEditingTitle = () => {
    isEditingTitle.value = false
    editingTitle.value = ''
  }

  const handleTitleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      saveTitle()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      cancelEditingTitle()
    }
  }
</script>

<template>
  <div
    v-if="questionnaire"
    class="flex h-[calc(100vh-60px)] flex-col bg-gray-50"
  >
    <!-- Header -->
    <div
      class="inline-flex w-full items-center justify-between border-b border-gray-200 bg-white p-4"
    >
      <!-- Left Side -->
      <div class="inline-flex flex-col items-start justify-center gap-1">
        <!-- Back Link -->
        <div class="inline-flex items-start justify-start gap-1">
          <div class="relative h-5 w-5 overflow-hidden">
            <ChevronLeft class="absolute left-[4.17px] top-[4.17px] h-3 w-3" />
          </div>
          <button
            class="justify-start font-['Inter'] text-sm font-medium leading-tight text-gray-500 underline
              hover:text-gray-700"
            @click="$router.push(ROUTES.APP.SETTINGS.QUESTIONNAIRES.QUESTIONNAIRE_LIST)"
          >
            {{ t('backToAllQuestionnaires') }}
          </button>
        </div>

        <!-- Title with Edit Icon -->
        <div class="inline-flex items-center justify-start gap-2">
          <!-- Editing Mode -->
          <div
            v-if="isEditingTitle"
            class="flex items-center space-x-2"
          >
            <input
              ref="titleInput"
              v-model="editingTitle"
              type="text"
              class="flex-1 rounded-md border-0 px-3 py-1.5 font-['Inter'] text-xl font-semibold text-gray-800 ring-1
                ring-inset ring-purple-300 focus:ring-2 focus:ring-inset focus:ring-purple-600"
              @blur="saveTitle"
              @keydown="handleTitleKeydown"
            />
            <button
              class="flex h-8 w-8 items-center justify-center text-green-600 hover:text-green-700"
              @click="saveTitle"
            >
              <CheckCircle2 class="h-4 w-4" />
            </button>
            <button
              class="flex h-8 w-8 items-center justify-center text-red-600 hover:text-red-700"
              @click="cancelEditingTitle"
            >
              <X class="h-4 w-4" />
            </button>
          </div>

          <!-- Display Mode -->
          <div
            v-else
            class="flex items-center space-x-2"
          >
            <div
              class="justify-start font-['Inter'] text-xl font-semibold leading-loose text-gray-800"
            >
              {{ questionnaire?.title || t('newQuestionnaire') }}
            </div>
            <button
              class="flex items-center justify-center overflow-hidden rounded-md bg-white p-1
                shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] outline outline-1 outline-offset-[-1px] outline-gray-200
                hover:bg-gray-50"
              @click="startEditingTitle"
            >
              <div class="relative h-4 w-4 overflow-hidden">
                <PencilIcon class="absolute left-[1.33px] top-[1.33px] h-3.5 w-3.5" />
              </div>
            </button>
          </div>
        </div>
      </div>

      <!-- Right Side - Action Buttons -->
      <div class="flex items-center justify-start gap-2">
        <!-- Generate with AI Button -->
        <Dialog v-model:open="isModalOpen">
          <DialogTrigger as-child>
            <button
              class="flex h-10 items-center justify-center overflow-hidden rounded-lg bg-white p-2
                shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] outline outline-1 outline-offset-[-1px] outline-gray-200
                hover:bg-gray-50"
              @click="isModalOpen = true"
            >
              <div class="relative h-6 w-6 overflow-hidden">
                <WandSparkles class="absolute left-[2px] top-[2px] h-5 w-5" />
              </div>
              <div class="flex items-center justify-center gap-2 px-2">
                <div
                  class="justify-start font-['Inter'] text-base font-medium leading-normal text-gray-800"
                >
                  {{ t('generateWithAI') }}
                </div>
              </div>
            </button>
          </DialogTrigger>
          <DialogContent
            v-if="isLoading"
            class="flex flex-col items-center"
          >
            <DialogTitle></DialogTitle>
            <div
              class="text-surface mt-2 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid
                border-current border-e-transparent align-[-0.125em]
                motion-reduce:animate-[spin_1.5s_linear_infinite]"
              role="status"
            >
              <span
                class="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0
                  ![clip:rect(0,0,0,0)]"
              >
                Loading...
              </span>
            </div>
            <DialogDescription>
              <h3 class="text-lg">{{ t('creatingQuestionnaire') }}</h3>
            </DialogDescription>
          </DialogContent>
          <DialogContent
            v-else
            class="sm:max-w-[425px]"
          >
            <DialogHeader>
              <DialogTitle>{{ t('createWithAI') }}</DialogTitle>
              <DialogDescription>
                {{ t('uploadToCreateQuestionnaire') }}
              </DialogDescription>
            </DialogHeader>
            <FileField
              v-model="uploadedFile"
              name="file"
              class="overflow-hidden"
              :allowed-file-extensions="['pdf', 'docx']"
              :hint="t('uploadHint')"
            />
            <Separator
              class="my-4"
              :label="t('or')"
            />
            <TextAreaField
              v-model="userPrompt"
              name="user_prompt"
              :placeholder="t('enterUserPrompt')"
            />
            <DialogFooter>
              <Button
                type="submit"
                :disabled="!uploadedFile && !userPrompt"
                @click="sendFile"
              >
                {{ t('create') }}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <!-- Preview Button -->
        <button
          class="flex h-10 items-center justify-center overflow-hidden rounded-lg bg-white p-2
            shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] outline outline-1 outline-offset-[-1px] outline-gray-200
            hover:bg-gray-50"
          @click="showFlowPreviewDialog = true"
        >
          <div class="relative h-6 w-6 overflow-hidden">
            <Play class="h-5 w-5 text-gray-700" />
          </div>
          <div class="flex items-center justify-center gap-2 px-2">
            <div
              class="justify-start font-['Inter'] text-base font-medium leading-normal text-gray-800"
            >
              {{ t('preview') }}
            </div>
          </div>
        </button>

        <!-- Duplicate Button -->
        <button
          class="flex h-10 items-center justify-center overflow-hidden rounded-lg bg-white p-2
            shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] outline outline-1 outline-offset-[-1px] outline-gray-200
            hover:bg-gray-50"
          @click="duplicateQuestionnaire"
        >
          <div class="relative h-6 w-6 overflow-hidden">
            <Copy class="absolute left-[2px] top-[2px] h-5 w-5" />
          </div>
          <div class="flex items-center justify-center gap-2 px-2">
            <div
              class="justify-start font-['Inter'] text-base font-medium leading-normal text-gray-800"
            >
              {{ t('duplicate') }}
            </div>
          </div>
        </button>

        <!-- Save Button -->
        <button
          class="flex h-10 items-center justify-center overflow-hidden rounded-lg bg-purple-600 p-2
            shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)] hover:bg-purple-700"
          @click="saveQuestionnaire()"
        >
          <div class="flex items-center justify-center gap-2 px-2">
            <div
              class="justify-start font-['Inter'] text-base font-medium leading-normal text-white"
            >
              {{ t('saveQuestionnaire') }}
            </div>
          </div>
        </button>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex flex-1 overflow-hidden">
      <!-- Sidebar -->
      <UserQuestionnaireSidebar
        v-if="questionnaire"
        :questionnaire="questionnaire"
        :is-open="isSidebarOpen"
        :is-new-questionnaire="isNewQuestionnaire"
        @toggle="toggleSidebar"
      />

      <!-- Collapsed Sidebar -->
      <div
        v-if="!isSidebarOpen && questionnaire"
        class="h-full w-16 flex-shrink-0 border-r border-gray-200 bg-white shadow-sm"
        style="box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.04)"
      >
        <div class="flex h-full flex-col items-center gap-4 p-4">
          <!-- Expand Button -->
          <button
            class="flex items-center justify-center text-gray-500 transition-colors hover:text-gray-700"
            style="
              height: 32px;
              padding: 6px;
              border-radius: 6px;
              border: 1px solid #e5e7eb;
              background: #fff;
              box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.04);
            "
            :title="t('showSidebar')"
            @click="toggleSidebar"
          >
            <ChevronsRight class="h-4 w-4" />
          </button>
        </div>
      </div>

      <!-- Main Content Area -->
      <div
        class="flex-1 overflow-y-auto transition-all duration-300"
        :class="{ 'w-full': !isSidebarOpen }"
      >
        <div class="p-6">
          <!-- Questionnaire Details Section -->
          <div
            v-if="selectedItem === 'questionnaire-details'"
            class="mb-6 rounded-lg border border-gray-200 bg-white p-6"
          >
            <h2 class="mb-4 text-lg font-medium text-gray-900">{{ t('questionnaireDetails') }}</h2>
            <p class="mb-6 text-sm text-gray-500">{{ t('generalInformation') }}</p>

            <div class="space-y-4">
              <div>
                <InputField
                  v-model="questionnaire.title"
                  :label="t('title')"
                  name="title"
                  :placeholder="t('newQuestionnairePlaceholder')"
                />
              </div>

              <div class="grid grid-cols-2 gap-4">
                <div>
                  <SelectField
                    v-model="questionnaire.law_field"
                    :label="t('lawField')"
                    name="law_field"
                    :options="lawFieldsOptions"
                  />
                </div>

                <div>
                  <SelectField
                    v-model="questionnaire.law_topic"
                    :label="t('lawTopic')"
                    name="law_topic"
                    :options="lawTopicOptions"
                    :placeholder="t('notSelected')"
                  />
                </div>
              </div>

              <!-- <div>
                <TextAreaField
                  v-model="questionnaire.description"
                  :label="t('internalDescription')"
                  name="internal_description"
                  rows="3"
                />
              </div> -->
            </div>
          </div>

          <!-- Selected Section -->
          <div v-else-if="questionnaire.schema?.find((section) => section.uuid === selectedItem)">
            <UserQuestionnaireSection
              :questionnaire="questionnaire"
              :section-uuid="selectedItem"
            />
          </div>

          <!-- Selected Question -->
          <div v-else-if="getQuestionByUuid(selectedItem)">
            <UserQuestionnaireQuestionView
              :questionnaire="questionnaire"
              :question-uuid="selectedItem"
            />
          </div>

          <!-- Selected Virtual Question -->
          <div v-else-if="getVirtualQuestionSectionUuid(selectedItem)">
            <UserQuestionnaireVirtualQuestionView
              :questionnaire="questionnaire"
              :section-uuid="getVirtualQuestionSectionUuid(selectedItem)!"
            />
          </div>

          <!-- Fallback for when no valid selection -->
          <div
            v-else
            class="flex h-64 items-center justify-center text-gray-500"
          >
            <p>{{ t('selectSectionFromSidebar') }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Unsaved Changes Dialog -->
  <UnsavedChangesDialog
    v-model:open="dialogState.show.value"
    :title="t('changesNotSaved')"
    :description="t('unsavedChangesDescription')"
    :confirm-text="t('discardChanges')"
    :cancel-text="t('goBack')"
    @confirm="dialogState.onConfirm?.value?.()"
    @cancel="dialogState.onCancel?.value?.()"
  />

  <!-- Validation Error Dialog -->
  <ValidationErrorDialog
    v-model:open="showValidationErrorDialog"
    :errors="validationErrors"
    @navigate-to-error="navigateToError"
  />

  <!-- Flow Preview Dialog -->
  <QuestionnaireFlowPreviewDialog
    v-model:open="showFlowPreviewDialog"
    :schema="questionnaire?.schema || []"
  />
</template>
